/**
 * Travel Analytics Module Permissions
 *
 * Permission naming convention: Travel:{Module}:{Action}
 *
 * Providers and revenue are their own modules (rather than `Travel:Analytics:Providers:View`)
 * to keep every code at exactly three segments, so each needs its own wildcard:
 * `Travel:Analytics:*` does NOT cover `Travel:AnalyticsProviders:View`.
 */
export enum TravelAnalyticsPermissions {
  ALL = "Travel:Analytics:*",
  VIEW = "Travel:Analytics:View",
  EXPORT = "Travel:Analytics:Export",
  PROVIDERS_ALL = "Travel:AnalyticsProviders:*",
  PROVIDERS_VIEW = "Travel:AnalyticsProviders:View",
  REVENUE_ALL = "Travel:AnalyticsRevenue:*",
  REVENUE_VIEW = "Travel:AnalyticsRevenue:View",
}

/**
 * Check if user has access to travel analytics
 */
export function hasTravelAnalyticsAccess(userPermissions: string[]): boolean {
  return userPermissions.some((p) =>
    [TravelAnalyticsPermissions.VIEW, TravelAnalyticsPermissions.ALL, "*"].includes(
      p as TravelAnalyticsPermissions
    )
  );
}

/**
 * Check if user can export analytics data
 */
export function canExportAnalytics(userPermissions: string[]): boolean {
  return userPermissions.some((p) =>
    [TravelAnalyticsPermissions.EXPORT, TravelAnalyticsPermissions.ALL, "*"].includes(
      p as TravelAnalyticsPermissions
    )
  );
}

/**
 * Check if user can view provider analytics
 */
export function canViewProviderAnalytics(userPermissions: string[]): boolean {
  return userPermissions.some((p) =>
    [
      TravelAnalyticsPermissions.PROVIDERS_VIEW,
      TravelAnalyticsPermissions.PROVIDERS_ALL,
      "*",
    ].includes(p as TravelAnalyticsPermissions)
  );
}

/**
 * Check if user can view revenue metrics
 */
export function canViewRevenueMetrics(userPermissions: string[]): boolean {
  return userPermissions.some((p) =>
    [
      TravelAnalyticsPermissions.REVENUE_VIEW,
      TravelAnalyticsPermissions.REVENUE_ALL,
      "*",
    ].includes(p as TravelAnalyticsPermissions)
  );
}
